<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import BetaPageImageField from '@/components/beta-dashboard/molecules/BetaPageImageField.vue'
import { useMediaManagerStore } from '@/stores/modal/media-manager'
import type {
    BetaAdvertisement,
    BetaAdvertisementInput,
    BetaAdvertisementPosition,
    BetaAdvertisementType,
} from '@/types/beta-dashboard'

const props = defineProps<{
    open: boolean
    advertisement?: BetaAdvertisement | null
    saving?: boolean
}>()

const emit = defineEmits<{
    close: []
    save: [advertisement: BetaAdvertisementInput]
}>()

const mediaManagerStore = useMediaManagerStore()
const error = ref('')
const draft = ref<BetaAdvertisementInput>(emptyAdvertisement())

function emptyAdvertisement(): BetaAdvertisementInput {
    return {
        title: '',
        position: 'menu',
        advertisementType: 'text',
        content: '',
        image: '',
        ref: '',
        urlImage: '',
    }
}

watch(
    [() => props.open, () => props.advertisement],
    ([open, advertisement]) => {
        if (!open) return

        draft.value = advertisement
            ? JSON.parse(JSON.stringify(advertisement))
            : emptyAdvertisement()
        error.value = ''
    },
    { immediate: true },
)

watch(
    () => draft.value.position,
    (position) => {
        if (position !== 'menu' && draft.value.advertisementType === 'text') {
            draft.value.advertisementType = 'banner'
        }
    },
)

const isEditing = computed(() => Boolean(props.advertisement?.id))
const needsContent = computed(() => ['text', 'script'].includes(draft.value.advertisementType))

const chooseImage = () => {
    mediaManagerStore.setMaxSelectedMedias(1)
    mediaManagerStore.setAction((media: any) => {
        if (!media?.content) return
        draft.value.image = media.content
    })
    mediaManagerStore.setIsOpenModal(true)
}

const close = () => {
    if (props.saving || mediaManagerStore.isOpenModal) return
    emit('close')
}

const save = () => {
    const title = draft.value.title.trim()
    if (!title) {
        error.value = 'Informe o título da publicidade.'
        return
    }
    if (draft.value.advertisementType === 'banner' && !draft.value.image) {
        error.value = 'Selecione uma imagem para a publicidade.'
        return
    }
    if (needsContent.value && !draft.value.content.trim()) {
        error.value = 'Preencha o conteúdo da publicidade.'
        return
    }

    error.value = ''
    emit('save', JSON.parse(JSON.stringify({ ...draft.value, title })))
}

const setPosition = (event: Event) => {
    draft.value.position = (event.target as HTMLSelectElement).value as BetaAdvertisementPosition
}

const setType = (event: Event) => {
    draft.value.advertisementType = (event.target as HTMLSelectElement).value as BetaAdvertisementType
}
</script>

<template>
    <Teleport to="body">
        <div v-if="open" class="beta-advertisement-dialog" role="dialog" aria-modal="true">
            <button type="button" class="beta-advertisement-dialog__backdrop" aria-label="Fechar" @click="close"></button>

            <form class="beta-advertisement-dialog__panel" @submit.prevent="save">
                <header>
                    <div>
                        <p>Publicidade</p>
                        <h2>{{ isEditing ? 'Editar publicidade' : 'Nova publicidade' }}</h2>
                    </div>
                    <button type="button" class="is-close" aria-label="Fechar" @click="close">×</button>
                </header>

                <div class="beta-advertisement-dialog__body">
                    <label class="beta-advertisement-dialog__field">
                        <span>Título</span>
                        <input v-model="draft.title" type="text" maxlength="120">
                    </label>

                    <div class="beta-advertisement-dialog__columns">
                        <label class="beta-advertisement-dialog__field">
                            <span>Posição</span>
                            <select :value="draft.position" @change="setPosition">
                                <option value="menu">Menu do topo</option>
                                <option value="after_content">Depois do conteúdo</option>
                                <option value="footer">Rodapé</option>
                                <option value="sidebar">Menu lateral</option>
                            </select>
                        </label>

                        <label class="beta-advertisement-dialog__field">
                            <span>Tipo</span>
                            <select :value="draft.advertisementType" @change="setType">
                                <option v-if="draft.position === 'menu'" value="text">Texto</option>
                                <option value="banner">Imagem</option>
                                <option value="script">Código incorporado</option>
                            </select>
                        </label>
                    </div>

                    <label v-if="needsContent" class="beta-advertisement-dialog__field">
                        <span>{{ draft.advertisementType === 'script' ? 'Código incorporado' : 'Texto' }}</span>
                        <textarea v-model="draft.content" rows="5" spellcheck="false"></textarea>
                    </label>

                    <template v-else>
                        <BetaPageImageField
                            label="Imagem da publicidade"
                            description="Selecione a imagem exibida nesta posição"
                            :image="draft.image"
                            @select="chooseImage"
                            @remove="draft.image = ''"
                        />

                        <label class="beta-advertisement-dialog__field">
                            <span>URL de destino</span>
                            <input v-model="draft.ref" type="url" placeholder="https://">
                        </label>
                    </template>

                    <p v-if="error" class="beta-advertisement-dialog__error">{{ error }}</p>
                </div>

                <footer>
                    <button type="button" class="is-cancel" :disabled="saving" @click="close">Cancelar</button>
                    <button type="submit" class="is-save" :disabled="saving">
                        {{ saving ? 'Salvando…' : isEditing ? 'Salvar alterações' : 'Adicionar' }}
                    </button>
                </footer>
            </form>
        </div>
    </Teleport>
</template>

<style scoped>
.beta-advertisement-dialog {
    position: fixed;
    inset: 0;
    z-index: 110;
    display: grid;
    place-items: center;
    padding: 18px;
}

.beta-advertisement-dialog__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(20, 35, 57, 0.64);
    backdrop-filter: blur(7px);
}

.beta-advertisement-dialog__panel {
    position: relative;
    display: grid;
    width: min(590px, 100%);
    max-height: calc(100vh - 36px);
    grid-template-rows: auto minmax(0, 1fr) auto;
    overflow: hidden;
    border-radius: 22px;
    background: #fff;
}

.beta-advertisement-dialog__panel > header,
.beta-advertisement-dialog__panel > footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 18px;
}

.beta-advertisement-dialog__panel > header {
    border-bottom: 1px solid #e8edf3;
}

.beta-advertisement-dialog__panel > header p {
    color: var(--main-color);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
}

.beta-advertisement-dialog__panel > header h2 {
    margin-top: 2px;
    color: #172d4a;
    font-size: 21px;
    font-weight: 700;
}

.beta-advertisement-dialog__panel .is-close {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: #f0f4f9;
    color: #60718a;
    font-size: 18px;
}

.beta-advertisement-dialog__body {
    display: grid;
    min-height: 0;
    align-content: start;
    gap: 12px;
    overflow-y: auto;
    padding: 15px 18px;
}

.beta-advertisement-dialog__columns {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
}

.beta-advertisement-dialog__field {
    display: grid;
    gap: 6px;
}

.beta-advertisement-dialog__field > span {
    color: #3a506d;
    font-size: 12px;
    font-weight: 700;
}

.beta-advertisement-dialog__field input,
.beta-advertisement-dialog__field select,
.beta-advertisement-dialog__field textarea {
    width: 100%;
    border: 1px solid #dce4ef;
    border-radius: 12px;
    background: #fff;
    padding: 10px 11px;
    color: #172d4a;
    font-size: 11px;
    outline: none;
}

.beta-advertisement-dialog__field input:focus,
.beta-advertisement-dialog__field select:focus,
.beta-advertisement-dialog__field textarea:focus {
    border-color: var(--main-color);
    outline: 3px solid color-mix(in srgb, var(--main-color) 9%, transparent);
    outline-offset: 1px;
}

.beta-advertisement-dialog__error {
    color: #b24c4c;
    font-size: 10px;
    font-weight: 700;
}

.beta-advertisement-dialog__panel > footer {
    justify-content: flex-end;
    border-top: 1px solid #e8edf3;
    background: #fbfcfe;
}

.beta-advertisement-dialog__panel > footer button {
    min-height: 38px;
    border-radius: var(--beta-button-radius);
    padding: 0 15px;
    font-size: 10px;
    font-weight: 700;
}

.beta-advertisement-dialog__panel > footer .is-cancel {
    color: #687b94;
}

.beta-advertisement-dialog__panel > footer .is-save {
    background: var(--main-color);
    color: #fff;
}

.beta-advertisement-dialog__panel button:disabled {
    cursor: wait;
    opacity: 0.55;
}

@media (max-width: 560px) {
    .beta-advertisement-dialog {
        place-items: end center;
        padding: 0;
    }

    .beta-advertisement-dialog__panel {
        max-height: 94vh;
        border-radius: 22px 22px 0 0;
    }

    .beta-advertisement-dialog__columns {
        grid-template-columns: 1fr;
    }
}
</style>
